import { useEffect, useState, type RefObject } from 'react';

/** Bump when the rendered size or framing changes, so old previews are replaced. */
const CACHE_PREFIX = 'indusketch.thumb2.';

const memory = new Map<string, string>();
let queue: Promise<unknown> = Promise.resolve();

function readCache(key: string): string | null {
  const hit = memory.get(key);
  if (hit) return hit;
  try {
    return localStorage.getItem(CACHE_PREFIX + key);
  } catch {
    return null;
  }
}

function writeCache(key: string, url: string) {
  memory.set(key, url);
  try {
    localStorage.setItem(CACHE_PREFIX + key, url);
  } catch {
    // Storage full or disabled: thumbnails are regenerated next session, nothing breaks.
  }
}

/**
 * Preview image of an uploaded model. `version` (e.g. the asset's creation time) invalidates
 * the browser cache when a model is replaced. Returns null until rendered or on failure.
 * The 3D renderer is loaded on first use, so pages with cached previews never load Three.js.
 */
export function useAssetThumbnail(assetId: string, version: string, enabled: boolean): string | null {
  const key = `${assetId}.${version}`;
  const [url, setUrl] = useState<string | null>(() => readCache(key));
  useEffect(() => {
    // Only visible rows render: a large library must not download every model up front.
    if (url || !enabled) return;
    let active = true;
    // One after another: large models are parsed on the main thread.
    const job = queue.then(async () => {
      const { renderThumbnail } = await import('../editor/scene3d/assetThumbnails.js');
      return renderThumbnail(assetId);
    });
    queue = job.catch(() => undefined);
    job
      .then((result) => {
        writeCache(key, result);
        if (active) setUrl(result);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [assetId, key, url, enabled]);
  return url;
}

/** True once the element has been on screen (collapsed groups and scrolled-away rows stay false). */
export function useSeenOnScreen(ref: RefObject<Element | null>): boolean {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setSeen(true);
    });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, seen]);
  return seen;
}
